import { useState, useEffect, useCallback } from 'react'
import type { GalleryImage } from '../types/config'
import { fetchGallery } from '../api/client'

interface UseGalleryReturn {
  images: GalleryImage[]
  loading: boolean
  error: string | null
  reload: () => Promise<void>
  addImage: (image: GalleryImage) => void
}

export function useGallery(): UseGalleryReturn {
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchGallery()
      // Sort by createdAt descending (newest first)
      const sorted = data.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      setImages(sorted)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const addImage = useCallback((image: GalleryImage) => {
    setImages(prev => [image, ...prev])
  }, [])

  return { images, loading, error, reload: load, addImage }
}
